import React, { useEffect } from "react";
import { Button, Form, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Controller, useForm } from "react-hook-form";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle01Icon } from "@hugeicons/core-free-icons";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useResolveIncidentReportMutation } from "@/services/reports";
import { handleFormErrors } from "@/utils/helpers";

const emptyValues = {
  reported_person_name: "",
  report_authority_id: null,
};

/**
 * End-case flow: POST {system}/incident-reports/:id/resolve.
 * Owns form state, validation, and submit — parent only passes the open row + lookups.
 */
export const ResolveIncidentReportModal = ({
  report,
  onClose,
  reportAuthorityOptions,
  system,
}) => {
  const { t } = useTranslation();
  const isOpen = Boolean(report?.id != null);
  const { mutate, isPending } = useResolveIncidentReportMutation({ system });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!report?.id) {
      reset(emptyValues);
      return;
    }
    reset({
      reported_person_name: "",
      report_authority_id: null,
    });
  }, [report, reset]);

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const onValidSubmit = (values) => {
    if (!report?.id) return;
    mutate(
      {
        id: report.id,
        reported_person_name: values.reported_person_name.trim(),
        report_authority_id: values.report_authority_id,
      },
      {
        onSuccess: () => {
          onClose();
          reset(emptyValues);
        },
        onError: (error) => {
          handleFormErrors(error, setError, t, "SOMETHING_WENT_WRONG");
        },
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      backdrop={isPending ? "static" : true}
    >
      <ModalHeader toggle={handleClose}>{t("CONFIRM")}</ModalHeader>
      <ModalBody>
        <form id="resolve-incident-report-form" onSubmit={handleSubmit(onValidSubmit)}>
          <Stack gap={3}>
            <Form.Group>
              <Label className="fw-bold">{t("NUSUK_COMPLETE_CASE_RECIPIENT")}</Label>
              <Controller
                name="reported_person_name"
                control={control}
                rules={{ required: t("FIELD_REQUIRED") }}
                render={({ field }) => (
                  <AntInput
                    {...field}
                    placeholder={t("ENTER_PLACEHOLDER")}
                    disabled={isPending}
                    status={errors.reported_person_name ? "error" : undefined}
                  />
                )}
              />
              {errors.reported_person_name && (
                <div className="text-danger small mt-1">
                  {errors.reported_person_name.message}
                </div>
              )}
            </Form.Group>
            <Form.Group>
              <Label className="fw-bold">{t("REPORT_AUTHORITY")}</Label>
              <Controller
                name="report_authority_id"
                control={control}
                rules={{ required: t("FIELD_REQUIRED") }}
                render={({ field }) => (
                  <AntSelect
                    showSearch
                    optionFilterProp="label"
                    placeholder={t("NUSUK_REPORT_AUTHORITY_LIST_PLACEHOLDER")}
                    options={reportAuthorityOptions}
                    value={field.value ?? undefined}
                    onChange={(v) => field.onChange(v ?? null)}
                    disabled={isPending}
                    status={errors.report_authority_id ? "error" : undefined}
                  />
                )}
              />
              {errors.report_authority_id && (
                <div className="text-danger small mt-1">
                  {errors.report_authority_id.message}
                </div>
              )}
            </Form.Group>
            <p className="mb-0 small text-muted">{t("CONFIRM_END_CASE_MESSAGE")}</p>
          </Stack>
        </form>
      </ModalBody>
      <ModalFooter className="d-flex gap-2 justify-content-between flex-wrap">
        <Button
          variant="light"
          type="button"
          className="border"
          disabled={isPending}
          onClick={handleClose}
        >
          {t("CANCEL")}
        </Button>
        <Button
          variant="danger"
          type="submit"
          form="resolve-incident-report-form"
          disabled={isPending}
        >
          <span className="d-inline-flex align-items-center gap-1">
            <HugeiconsIcon size={18} icon={CheckmarkCircle01Icon} />
            {t("END_CASE")}
          </span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};
